import React, { useState, useCallback, useEffect } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import api from "../../../services/api";
import Navbar from "../../../components/Navbar.js";
import ProjectDetails from "./PFRSections/ProjectDetails.js";
import ExcecutiveSummary from "./PFRSections/ExcecutiveSummary.js";
import ProjectIntro from "./PFRSections/ProjectIntro.js";
import ProjectDescription from "./PFRSections/ProjectDescription.js";
import SiteAnalysis from "./PFRSections/SiteAnalysis.js";
import BriefPlanning from "./PFRSections/BriefPlanning.js";
import ProposedInfrastructure from "./PFRSections/ProposedInfrastructure.js";
import RnRPlan from "./PFRSections/RnRPlan.js";
import ProjectScheduleCost from "./PFRSections/ProjectScheduleCost.js";
import FinalRecommendation from "./PFRSections/FinalRecommendation.js";

const PFR = () => {
  const [userRole, setUserRole] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [pfrFormData, setPfrFormData] = useState({
    projectDetails: {
      projectName: "",
      projectSector: "",
      projectCategory: "",
      projectCity: "",
      projectTehsil: "",
      projectDistrict: "",
      projectArea: "",
      projectKhasraNo: "",
      projectProductionCapacity: "",
      projectLeasePeriod: "",
      projectLocation: "",
      applicantName: "",
      applicantAddress: "",
      applicantEmail: "",
      applicantMobile: "",
    },
    excecutiveSummary: {
      loiRefNo: "",
      minePlanApprovalRefNo: "",
      clusterInfo: "",
      oldExcavatedPitInfo: "",
      ecologicallySensitiveAreas: "",
      reservedForest: "",
      waterBodies: "",
      connectivity: "",
      lithology: "",
      topography: "",
      geologicalReserves: "",
      miningMethod: "",
      machineriesUsed: "",
      benchHeightWidth: "",
      totalSoilGeneration: "",
      wasteGeneration: "",
      waterWastewater: "",
      depthOfMining: "",
      plantationGreenBelt: "",
      projectCost: "",
    },
    projectIntro: {
      identification: "",
      description: "",
      need: "",
      demandSupplyGap: "",
      importsVsIndigenous: "",
      exportPossibility: "",
      domesticExportMarkets: "",
      employmentGeneration: "",
      minningMate: "",
      chowkidars: "",
      unskilledLabors: "",
      skilledLabors: "",
      totalWorkers: "",
    },
    projectDescription: {
      typeOfProject: "",
      detailsOfAlternateSites: "",
      sizeOrMagnitudeOfOperation: "",
      projectDescriptionWithProcessDetails: "",
      resourceOptimization: "",
      availabilityOfWater: "",
    },
    siteAnalysis: {
      connectivity: "",
      landForm: "",
      typeOfLand: "",
      topography: "",
      existingLandUsePattern: "",
      soilClassification: "",
      rainfallAndClimate: "",
      socialInfrastructureAvailable: "",
    },
    briefPlanning: {
      planningConcept: "",
      populationProjection: "",
      landUsePlanning: "",
      assessmentOfInfrastructureDemand: "",
      amenitiesAndFacilities: "",
    },
    proposedInfrastructure: {
      industrialArea: "",
      residentialArea: "",
      greenbelt: "",
      socialInfrastructure: "",
      proposedInfrastructureConnectivity: "",
      drinkingWaterManagement: "",
      sewerageSystem: "",
      industrialWasteManagement: "",
      solidWasteManagement: "",
      powerSupply: "",
    },
    rnrPlan: {
      rnrPlan: "",
    },
    projectScheduleCost: {
      projectSchedule: "",
      projectCost: "",
    },
    finalRecommendation: {
      recommendation: "",
    },
  });

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData && userData.role) {
          setUserRole(userData.role);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    const fetchExistingPFR = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const projectId = urlParams.get("projectId");

        if (!projectId) return;

        const response = await api.get(
          `/api/projects/${projectId}/created-documents`
        );

        if (response.data && response.data.documents) {
          const pfrDocs = response.data.documents.filter(
            (doc) => doc.type === "pfr"
          );

          if (pfrDocs.length > 0) {
            const pfrData = await api.get(`/api/pfr/${pfrDocs[0].id}`);

            if (
              pfrData.data &&
              pfrData.data.document &&
              pfrData.data.document.content
            ) {
              setPfrFormData(pfrData.data.document.content);

              toast.info(
                "Loaded existing PFR data. Changes will update the existing document."
              );
            }
          }
        }
      } catch (error) {
        console.error("Error fetching existing PFR:", error);
      }
    };

    fetchUserRole();
    fetchExistingPFR();
  }, []);

  const role = userRole;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get("projectId");

    if (!projectId) {
      toast.error("Missing project ID. Please start from the project page.");
      return;
    }

    try {
      const loadingToast = toast.info("Generating document, please wait...", {
        autoClose: false,
      });

      await api.post("/api/pfr", {
        projectId: projectId,
        title:
          pfrFormData.projectDetails.projectName || "Pre-Feasibility Report",
        content: pfrFormData,
      });

      const response = await api.post("/generate-pfr", pfrFormData, {
        responseType: "blob",
      });

      toast.dismiss(loadingToast);

      const filename = `PFR_${
        pfrFormData.projectDetails.projectName || "Document"
      }_${new Date().toISOString().slice(0, 10)}.docx`;

      saveAs(new Blob([response.data]), filename);

      toast.success("Document generated and saved successfully!");
      window.history.back();
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error("Failed to generate document. Please try again.");
    }
  };

  const handleFormDataChange = useCallback((section, data) => {
    setPfrFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }));
  }, []);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProjectDetails
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 2:
        return (
          <ExcecutiveSummary
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 3:
        return (
          <ProjectIntro
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 4:
        return (
          <ProjectDescription
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 5:
        return (
          <SiteAnalysis
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 6:
        return (
          <BriefPlanning
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 7:
        return (
          <ProposedInfrastructure
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 8:
        return (
          <RnRPlan
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 9:
        return (
          <ProjectScheduleCost
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      case 10:
        return (
          <FinalRecommendation
            pfrFormData={pfrFormData}
            setPfrFormData={handleFormDataChange}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="caf-container">
      <Navbar role={role} />
      <div className="step-indicator">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((step) => (
          <div
            key={step}
            className={`step ${currentStep === step ? "active" : ""} 
                     ${currentStep > step ? "completed" : ""}`}
            onClick={() => setCurrentStep(step)}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1}
              {step === 2}
              {step === 3}
              {step === 4}
              {step === 5}
              {step === 6}
              {step === 7}
              {step === 8}
              {step === 9}
              {step === 10}
            </div>
          </div>
        ))}
      </div>
      <h1 className="pfr-heading">Project Feasibility Report</h1>
      <form className="pfr-form" onSubmit={handleSubmit}>
        {renderStep()}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep((prev) => prev - 1)}
              className="prev-btn"
            >
              Previous
            </button>
          )}
          {currentStep < 10 && (
            <button
              type="button"
              onClick={() => setCurrentStep((prev) => prev + 1)}
              className="next-btn"
            >
              Next
            </button>
          )}
          {currentStep === 10 && (
            <button type="submit" className="submit-btn">
              Submit
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PFR;
